import { claimflowApiClient } from './claimflowApiClient';
import type { WorkflowStepExecutionSummary } from '../components/WorkflowStepTracker';
import type { ProvisionToRecoverRow } from '../types';

interface ErpProxyResponse<T> {
  success: boolean;
  http_status: number;
  data: T;
  error?: { code: string; message: string } | null;
}

export interface VeosClaimSummary {
  ID_SIN: number;
  TIERS: string | null;
  NCBT_SIN: string;
  PASS_NOM_PER: string;
  PAPP_NOM_PER: string;
  NCBT_NCIE: string;
  DATO_SIN: number;
  DATS_SIN: number;
  LIB_MVT: string;
  IMMAT_VEH: string | null;
  LIEU: string;
  TXRESP_SIN: number;
  CD_IDA: string | null;
  LIB_IDA: string | null;
  CD_CIRC: string;
  LIB_CIRC: string;
  DESC: string | null;
  TYP_SIN: string;
  TYP_SIN_LIB: string;
  GESTIONNAIRE: string | null;
  GESTCORPO: string | null;
  CB_GESTION: string | null;
  CB_GESTION_1ET2: string | null;
}

interface VeosClaimSummaryResponse {
  total: number;
  data: VeosClaimSummary[];
}

/**
 * One `WS2_GS_TAB_SINGAR` row. Per garantie the API returns one main row
 * (`CD_PSD === 'TOTAL'`) followed by its detail rows per poste (`CD_PSD` =
 * ASSDC / ASSCC / HON / DIVC / ASSFRC…, label in `LIB_PSD`), ordered by
 * `ORDRE` (mahhp 22854).
 */
export interface VeosCostLine {
  ID_SIN: number;
  ID_SGAR: number;
  CD_GAR: string;
  LIB_GAR: string;
  /** `'TOTAL'` on the garantie's main row, else the poste code. */
  CD_PSD: string;
  /** Poste label (`'TOTAL'` on the main row). */
  LIB_PSD: string;
  /** Sort key (garantie label + poste rank) — the API already returns rows in this order. */
  ORDRE: string;
  PROV: number;
  PROV_RESTANTE: number;
  REG: number | null;
  RECEST: number;
  RECPROV: number;
  RECREG: number | null;
  FRANCH_SGAR: number;
  PROVFRC: number;
  PROVFRCJH: number | null;
  REGFRC: number | null;
  COUT_CIE: number;
}

interface VeosCostResponse {
  total: number;
  data: VeosCostLine[];
}

interface VeosProvisionsToRecoverResponse {
  total: number;
  data: ProvisionToRecoverRow[];
}

export interface VeosGuaranteeToClose {
  CD_GAR: string;
  LIB_GAR: string;
}

interface VeosGuaranteesToCloseResponse {
  total: number;
  data: VeosGuaranteeToClose[];
}

export interface VeosGuaranteeToOpen {
  CD_GAR: string;
  LIB_GAR: string;
}

interface VeosGuaranteesToOpenResponse {
  total: number;
  data: VeosGuaranteeToOpen[];
}

export interface VeosMissingPiece {
  ID_SIN: number;
  ID_PMD: number;
  CD_PIE: string;
  LIB_PIE: string;
  DATR_PMD: number | null; // epoch ms when RECU=1
  RECU: 0 | 1;
}

interface VeosMissingPiecesResponse {
  total: number;
  data: VeosMissingPiece[];
}

export interface VeosPieceToRequest {
  CD_PIE: string;
  LIB_PIE: string;
  EN_ATTENTE: 0 | 1;
  RECUE: 0 | 1;
  STATUT: string | null;
  ID_PMD: number | null;
}

interface VeosPiecesToRequestResponse {
  total: number;
  data: VeosPieceToRequest[];
}

/**
 * One task, as returned by `WS2_GS_SEL_TACHE` (origami slug
 * `boby-ws2-gs-sel-tache`, mahhp 22943) — feeds the task screen's rule
 * context and the closure card.
 */
export interface VeosTaskInfo {
  ID_EVENT: number | string;
  ID_SIN?: number | string;     // claim id
  DATE_DEBUT: string | number;  // epoch ms from VEOS ("DD/MM/YYYY" tolerated)
  PRENOM_PER: string;
  NOM_PER: string;
  ECRAN: string | null;
  CLASSEMENT?: string | null;      // classification code (e.g. "RE"); null when none
  CLASSEMENT_LIB?: string | null;  // classification label (e.g. "Rapport expertise matériel"); null when none
  LIBELLE_TYPE_TACHE?: string;     // task type label (e.g. "Email")
  STATUT?: string;                 // "EN COURS" (open) or "TERMINE" (closed)
}

interface VeosTaskInfoResponse {
  total: number;
  data: VeosTaskInfo[];
}

/**
 * One task of a claim, as listed by `WS2_GS_TAB_TACHES_SINISTRE` (origami
 * slug `boby-ws2-gs-tab-taches-sinistre`, mahhp 22862) — the « Tâches » card
 * of the claim screen. VEOS returns the open tasks first.
 */
export interface ClaimTaskRow {
  ID_SIN: number;
  /** The task id — the `:taskId` of its processing screen. */
  ID_EVENT: number;
  /** `"EN COURS"` (open) or `"TERMINE"` (closed). */
  STATUT: string;
  /** Start date, epoch ms. */
  DATE_DEBUT: number | null;
  LIBELLE: string | null;
  /** Classification label (e.g. "Gestion sinistre"). */
  CLASSEMENT_LIB: string | null;
  /** Task type label (e.g. "Email"). */
  LIBELLE_TYPE_TACHE: string | null;
  CATEGORIE: string | null;
  /** The recipient (user code / initials, e.g. "WS"). */
  NUM_PER: string | null;
  /** `"1"` when the task may be closed from the list. */
  ACTION_CLOTURE: string | null;
}

interface ClaimTasksResponse {
  total: number;
  data: ClaimTaskRow[];
}

/** One row of `WS2_GS_SEL_TITRE_TACHE` — the two pre-formatted title lines. */
export interface VeosTaskTitle {
  TITRE1: string | null;
  TITRE2: string | null;
}

interface VeosTaskTitleResponse {
  total: number;
  data: VeosTaskTitle[];
}

/** The task detail title (mahhp 22852), normalised (`null` lines → `''`). */
export interface TaskTitleData {
  titre1: string;
  titre2: string;
}

/** One row of `WS2_GS_SEL_MOTIF` — a claim "Statut / motif" choice. */
export interface VeosMotif {
  CD_MOT: string;
  LIB_MOT: string;
}

interface VeosMotifsResponse {
  total: number;
  data: VeosMotif[];
}

/** A "Statut / motif" select option (`code` = `CD_MOT`, `label` = `LIB_MOT`). */
export interface MotifOption {
  code: string;
  label: string;
}

/** Optional inputs of {@link taskService.closeTask}. */
export interface CloseTaskOptions {
  /** Claim id (`ID_SIN`) — needed by the workflow when a `motif` is set. */
  idSin?: string;
  /** Selected `CD_MOT`; empty / absent ⇒ the claim motif is left untouched. */
  motif?: string;
}

/** The execution resource returned by `POST /claimflow/workflows/{slug}/execute`. */
export interface WorkflowExecutionData {
  id: string;
  status: string;
  context_data?: Record<string, unknown>;
  step_executions?: unknown[];
  [key: string]: unknown;
}

const CLOSE_TASK_WORKFLOW_URL = '/claimflow/workflows/claimflow-close-task/execute';

/**
 * A failed workflow run (HTTP 422 from the workflow execute endpoint).
 *
 * Carries the backend `message` and the per-step trail mapped for
 * `WorkflowStepTracker`, so a card can render which step failed. The raw
 * transport error is kept as `cause`; the run's `error_code` (e.g.
 * `WORKFLOW_HALTED` for a `fail-workflow` step, `INPUT_VALIDATION_FAILED`) as
 * `errorCode`, `null` when the body carries none.
 */
export class WorkflowExecutionError extends Error {
  readonly stepExecutions: WorkflowStepExecutionSummary[];
  readonly errorCode: string | null;

  constructor(
    message: string,
    stepExecutions: WorkflowStepExecutionSummary[],
    options?: { cause?: unknown; errorCode?: string | null },
  ) {
    super(message, options === undefined ? undefined : { cause: options.cause });
    this.name = 'WorkflowExecutionError';
    this.stepExecutions = stepExecutions;
    this.errorCode = options?.errorCode ?? null;
  }
}

const TRACKED_STEP_STATUSES: ReadonlySet<string> = new Set<
  WorkflowStepExecutionSummary['status']
>(['pending', 'running', 'completed', 'failed', 'cancelled', 'skipped']);

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object'
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * Map the execution resource's `step_executions` to tracker rows. Steps the
 * engine skipped (a `condition` not met — e.g. the claim-motif steps when no
 * motif was chosen) are kept: the tracker shows them muted as « ignorée », so
 * the trail reads as the full workflow. A row with an unknown status is
 * dropped.
 */
function toStepSummaries(executions: unknown[]): WorkflowStepExecutionSummary[] {
  const steps: WorkflowStepExecutionSummary[] = [];
  for (const raw of executions) {
    const exec = asRecord(raw);
    if (!exec || typeof exec.status !== 'string') continue;
    if (!TRACKED_STEP_STATUSES.has(exec.status)) continue;
    const stepName = asRecord(exec.step)?.name;
    steps.push({
      name:
        typeof stepName === 'string'
          ? stepName
          : `Étape ${String(exec.workflow_step_id).slice(0, 8)}`,
      status: exec.status as WorkflowStepExecutionSummary['status'],
      errorMessage:
        typeof exec.error_message === 'string' ? exec.error_message : null,
    });
  }
  return steps;
}

/**
 * Turn a workflow-execute failure into a {@link WorkflowExecutionError} when
 * the response body describes the failed run (`{ message, data: { …,
 * step_executions } }`). Anything else (network error, 5xx without a body…)
 * yields `null` so the caller re-throws the original error untouched.
 *
 * Shared by every workflow-execute caller of the module (`closeTask` here,
 * `intervenantService.updateIntervenant`, `clotureService.closeClaim`,
 * `estimationSaveService.save`).
 */
export function toWorkflowExecutionError(error: unknown): WorkflowExecutionError | null {
  const body = asRecord(asRecord(asRecord(error)?.response)?.data);
  if (!body) return null;

  const execution = asRecord(body.data) ?? body;
  const executions = Array.isArray(execution.step_executions)
    ? execution.step_executions
    : null;
  const message = typeof body.message === 'string' ? body.message : null;
  if (executions === null && message === null) return null;

  return new WorkflowExecutionError(
    message ?? (error instanceof Error ? error.message : 'Workflow execution failed.'),
    executions === null ? [] : toStepSummaries(executions),
    {
      cause: error,
      errorCode: typeof execution.error_code === 'string' ? execution.error_code : null,
    },
  );
}

export const taskService = {
  async fetchClaimSummary(ses: number, idSin: number): Promise<VeosClaimSummary> {
    const response = await claimflowApiClient.get<ErpProxyResponse<VeosClaimSummaryResponse>>(
      '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-fic-sinistre',
      { params: { SES: ses, id_sin: idSin } },
    );

    if (!response.data.success) {
      throw new Error(response.data.error?.message ?? 'ERP request failed');
    }

    const [claim] = response.data.data.data;
    if (!claim) {
      throw new Error('Aucune fiche sinistre trouvée.');
    }

    return claim;
  },

  async fetchCosts(ses: number, idSin: number): Promise<VeosCostLine[]> {
    const response = await claimflowApiClient.get<ErpProxyResponse<VeosCostResponse>>(
      '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-tab-singar',
      { params: { SES: ses, id_sin: idSin, perPage: 9999 } },
    );

    if (!response.data.success) {
      throw new Error(response.data.error?.message ?? 'ERP request failed');
    }

    return response.data.data.data;
  },

  /**
   * Provisions "à encaisser" for a claim — the rows the RecoveryCard lists.
   *
   * The page fetches them (in addition to the card's own on-mount fetch) so
   * `recoverable_provisions_count` / `recoverable_provision_total` can be
   * exposed on the rule context: a `hide` rule is evaluated before the card
   * mounts, so the card's internal fetch is too late to drive it.
   */
  async fetchProvisionsToRecover(
    ses: number,
    idSin: number,
  ): Promise<ProvisionToRecoverRow[]> {
    const response = await claimflowApiClient.get<
      ErpProxyResponse<VeosProvisionsToRecoverResponse>
    >('/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-provision-a-encaisser', {
      params: { SES: ses, ID_SIN: idSin, perPage: 9999 },
    });

    if (!response.data.success) {
      throw new Error(response.data.error?.message ?? 'ERP request failed');
    }

    return response.data.data.data;
  },

  async fetchGuaranteesToClose(ses: number, idSin: number): Promise<VeosGuaranteeToClose[]> {
    const response = await claimflowApiClient.get<ErpProxyResponse<VeosGuaranteesToCloseResponse>>(
      '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-garanties-acloturer',
      { params: { SES: ses, id_sin: idSin, perPage: 9999 } },
    );

    if (!response.data.success) {
      throw new Error(response.data.error?.message ?? 'ERP request failed');
    }

    return response.data.data.data;
  },

  async fetchGuaranteesToOpen(ses: number, idSin: number): Promise<VeosGuaranteeToOpen[]> {
    const response = await claimflowApiClient.get<ErpProxyResponse<VeosGuaranteesToOpenResponse>>(
      '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-garanties-ouvrables',
      { params: { SES: ses, id_sin: idSin, perPage: 9999 } },
    );

    if (!response.data.success) {
      throw new Error(response.data.error?.message ?? 'ERP request failed');
    }

    return response.data.data.data;
  },

  async fetchMissingPieces(idSin: number): Promise<VeosMissingPiece[]> {
    const response = await claimflowApiClient.get<ErpProxyResponse<VeosMissingPiecesResponse>>(
      '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-tab-pmq-recues',
      { params: { id_sin: idSin, perPage: 9999 } },
    );

    if (!response.data.success) {
      throw new Error(response.data.error?.message ?? 'ERP request failed');
    }

    return response.data.data.data;
  },

  async receiveMissingPieces(idSin: number, idPmds: number[]): Promise<void> {
    await claimflowApiClient.post(
      '/claimflow/workflows/claimflow-receive-piece/execute',
      {
        context_data: {
          id_sin: idSin,
          pieces: idPmds.map((id_pmd) => ({ id_pmd })),
        },
      },
    );
  },

  async fetchPiecesToRequest(idSin: number): Promise<VeosPieceToRequest[]> {
    const response = await claimflowApiClient.get<ErpProxyResponse<VeosPiecesToRequestResponse>>(
      '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-tab-pmq-demandees',
      { params: { id_sin: idSin, perPage: 9999 } },
    );

    if (!response.data.success) {
      throw new Error(response.data.error?.message ?? 'ERP request failed');
    }

    return response.data.data.data;
  },

  async submitPieceRequests(
    idSin: number,
    toDemand: { cd_pie: string }[],
    toCancel: { id_pmd: number }[],
  ): Promise<void> {
    await claimflowApiClient.post(
      '/claimflow/workflows/claimflow-request-pieces/execute',
      {
        context_data: {
          id_sin: idSin,
          pieces_to_demander: toDemand,
          pieces_to_annuler: toCancel,
        },
      },
    );
  },

  /**
   * One task by its id (`WS2_GS_SEL_TACHE`, mahhp 22943), whatever its status
   * (open or closed) or assignee — the connected user's `WS2_GS_TACHES` list
   * it replaces misses closed tasks and other people's, which left the rule
   * context and the closure card empty. VEOS may repeat the same row
   * (`total: 2`), so the first row wins; `null` when the id is unknown.
   */
  async fetchTaskInfo(taskId: string): Promise<VeosTaskInfo | null> {
    const response = await claimflowApiClient.get<ErpProxyResponse<VeosTaskInfoResponse>>(
      '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-tache',
      { params: { id_event: taskId } },
    );

    if (!response.data.success) {
      throw new Error(response.data.error?.message ?? 'ERP request failed');
    }

    return response.data.data.data[0] ?? null;
  },

  /**
   * Every task of a claim (mahhp 22862 — the « Tâches » card), open ones
   * first (VEOS order). `idEvent` is the task currently open on the claim
   * screen; it is forwarded as `id_event` only when set (a task-less claim
   * screen sends `id_sin` alone).
   */
  async fetchClaimTasks(idSin: string, idEvent?: string): Promise<ClaimTaskRow[]> {
    const response = await claimflowApiClient.get<ErpProxyResponse<ClaimTasksResponse>>(
      '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-tab-taches-sinistre',
      {
        params: {
          id_sin: idSin,
          ...(idEvent ? { id_event: idEvent } : {}),
          perPage: 9999,
        },
      },
    );

    if (!response.data.success) {
      throw new Error(response.data.error?.message ?? 'ERP request failed');
    }

    return response.data.data.data;
  },

  /**
   * The two-line task detail title (mahhp 22852) — `TITRE1` / `TITRE2` from
   * `WS2_GS_SEL_TITRE_TACHE`. Resolves `null` when the ERP returns no row;
   * errors propagate so the caller can fall back to the legacy title.
   *
   * `suppressErrorToast`: the title is cosmetic and `TaskTitle` falls back to
   * the legacy h1 silently, so a failure must not raise the app-wide toast.
   */
  async fetchTaskTitle(idSin: string, idEvent: string): Promise<TaskTitleData | null> {
    const response = await claimflowApiClient.get<ErpProxyResponse<VeosTaskTitleResponse>>(
      '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-titre-tache',
      { params: { id_sin: idSin, id_event: idEvent }, suppressErrorToast: true },
    );

    if (!response.data.success) {
      throw new Error(response.data.error?.message ?? 'ERP request failed');
    }

    const [row] = response.data.data.data;
    if (!row) return null;

    return { titre1: row.TITRE1 ?? '', titre2: row.TITRE2 ?? '' };
  },

  /**
   * "Statut / motif" choices for a claim (`WS2_GS_SEL_MOTIF`, ~34 rows — VEOS
   * pages at 15, hence `perPage: 9999`).
   */
  async fetchMotifs(idSin: string): Promise<MotifOption[]> {
    const response = await claimflowApiClient.get<ErpProxyResponse<VeosMotifsResponse>>(
      '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-motif',
      { params: { id_sin: idSin, perPage: 9999 } },
    );

    if (!response.data.success) {
      throw new Error(response.data.error?.message ?? 'ERP request failed');
    }

    return response.data.data.data.map((row) => ({
      code: row.CD_MOT,
      label: row.LIB_MOT,
    }));
  },

  /**
   * Close a task through the `claimflow-close-task` backend workflow — one
   * call. Server-side the workflow first updates the claim motif when one is
   * given (GET sinistre → PUT sinistre with `motif`), then reads the closure
   * comment (`FIC_EVENT`) and closes the task (`task/update`).
   *
   * `id_sin` / `motif` are only sent when provided (non-empty), so a caller
   * passing just the task id closes the task without touching the claim.
   *
   * @throws {WorkflowExecutionError} when the workflow run fails (HTTP 422) —
   *   carries the backend message and the step trail.
   */
  async closeTask(
    idTask: string,
    options: CloseTaskOptions = {},
  ): Promise<WorkflowExecutionData> {
    const { idSin, motif } = options;
    const contextData: Record<string, string> = {
      id_event: idTask,
      ...(idSin ? { id_sin: idSin } : {}),
      ...(motif ? { motif } : {}),
    };

    try {
      const response = await claimflowApiClient.post<{ data: WorkflowExecutionData }>(
        CLOSE_TASK_WORKFLOW_URL,
        { context_data: contextData },
      );
      return response.data.data;
    } catch (error) {
      const workflowError = toWorkflowExecutionError(error);
      if (workflowError) throw workflowError;
      throw error;
    }
  },
};
